import { useMemo, useState } from "react";
import InterventionActionPlan from "@/components/InterventionActionPlan";
import DropoutRiskSchoolsModal from "@/components/DropoutRiskSchoolsModal";
import { deriveDropoutConfidence } from "@/utils/analyticsConfidence";
import { buildDataStatusChip } from "@/utils/dataFreshness";
import DashboardPageHeader from "@/components/DashboardPageHeader";
import "../pages/AnalyticsDropoutRiskTab.css";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? "—");

const suggestOwner = (block = {}) => {
  const blockName = block.block_name || block.entity_name || block.name;
  if (blockName) {
    if (block.risk_level === "High") return `Block Officer — ${blockName}`;
    if (block.risk_level === "Medium") return `Cluster Lead — ${blockName}`;
    return `School Complex — ${blockName}`;
  }
  if (block.risk_level === "High") return "District Education Officer";
  if (block.risk_level === "Medium") return "Block Education Officer";
  return "School Complex Lead";
};

const priorityBadge = (riskLevel, rate) => {
  if (riskLevel === "High") return { label: "Critical", className: "adr-danger" };
  if (riskLevel === "Medium" && rate >= 4.5) return { label: "Watch", className: "adr-warn" };
  if (riskLevel === "Medium") return { label: "Watch", className: "adr-warn" };
  return { label: "Low", className: "" };
};

const tableRiskBadge = (riskLevel) => {
  if (riskLevel === "High") return { label: "High", className: "adr-danger" };
  if (riskLevel === "Medium") return { label: "Medium", className: "adr-warn" };
  return { label: "Low", className: "" };
};

const AnalyticsDropoutRiskTab = ({
  dropoutData,
  loading = false,
  onRefresh,
  onGenerate,
  scopeLabel = "Pune District",
}) => {
  const [focusModal, setFocusModal] = useState(null);
  const summary = dropoutData?.summary;
  const blocks = dropoutData?.block_risk_data || [];

  const sortedByRate = useMemo(
    () => [...blocks].sort((a, b) => (b.dropout_rate ?? 0) - (a.dropout_rate ?? 0)),
    [blocks],
  );

  const top7 = useMemo(() => sortedByRate.slice(0, 7), [sortedByRate]);
  const maxRate = top7[0]?.dropout_rate || 1;
  const maxCount = Math.max(...blocks.map((b) => b.dropout_count || 0), 1);

  const highestRate = sortedByRate[0];
  const highestCount = useMemo(
    () => [...blocks].sort((a, b) => (b.dropout_count ?? 0) - (a.dropout_count ?? 0))[0],
    [blocks],
  );
  const lowestBlocks = useMemo(
    () => [...blocks].sort((a, b) => (a.dropout_rate ?? 0) - (b.dropout_rate ?? 0)).slice(0, 2),
    [blocks],
  );

  const allMigrationZero = blocks.length > 0 && blocks.every((b) => !b.migration_count);

  const donutStyle = useMemo(() => {
    const total = summary?.total_blocks || 1;
    const highPct = ((summary?.high_risk_count || 0) / total) * 100;
    const medPct = ((summary?.medium_risk_count || 0) / total) * 100;
    const split1 = highPct;
    const split2 = highPct + medPct;
    return {
      background: `conic-gradient(var(--adr-danger) 0 ${split1}%, var(--adr-secondary) ${split1}% ${split2}%, var(--adr-success) ${split2}% 100%)`,
    };
  }, [summary]);

  const confidencePct = useMemo(
    () => deriveDropoutConfidence(summary, blocks),
    [summary, blocks],
  );

  const commandTitle = useMemo(() => {
    const watchCount = Math.min(7, (summary?.high_risk_count || 0) + (summary?.medium_risk_count || 0) || top7.length);
    if (summary?.high_risk_count > 0) {
      return `Escalate ${summary.high_risk_count} high-risk blocks and schedule field visits across ${watchCount} priority areas.`;
    }
    return `Focus field visits on ${watchCount} priority blocks while keeping the district below the 3% dropout threshold.`;
  }, [summary, top7.length]);

  const signals = useMemo(() => {
    const items = [];
    if (highestRate) {
      items.push({
        title: "Highest risk cluster",
        text: `${highestRate.block} is materially above the district average at ${highestRate.dropout_rate}% and should be reviewed for attendance, transport, and transition gaps.`,
      });
    }
    if (highestCount) {
      items.push({
        title: "Largest affected base",
        text: `${highestCount.block} has the largest absolute count with ${fmt(highestCount.dropout_count)} learners, so even moderate rate reduction can recover more students.`,
      });
    }
    items.push({
      title: "Data quality alert",
      text: allMigrationZero
        ? "Migration values are showing zero across all blocks; verify data capture before district-level reporting."
        : "Validate migration and remark fields before finalizing district-level risk labels.",
    });
    if (lowestBlocks.length) {
      items.push({
        title: "Benchmark blocks",
        text: `${lowestBlocks.map((b) => b.block).join(" and ")} perform near zero dropout and can be used as benchmark process models.`,
      });
    }
    return items.slice(0, 4);
  }, [highestRate, highestCount, allMigrationZero, lowestBlocks]);

  const defaultRoot = [
    "Attendance irregularity and grade-transition risk in higher dropout blocks.",
    "Transport/access issues likely in remote and semi-rural pockets.",
    allMigrationZero
      ? "Migration data needs validation before finalizing risk labels."
      : "Cross-check migration counts against school remark registers.",
  ];

  const defaultRecs = top7.slice(0, 3).map(
    (b) => `Run school-wise dropout verification drive in ${b.block}.`,
  );

  const defaultActions = [
    `Assign BEO review for top ${Math.min(3, top7.length)} blocks within 7 days.`,
    "Publish block-wise evidence dashboard before monthly review.",
    allMigrationZero ? "Flag missing migration records to data-entry owners." : "Audit remark completeness for high-rate blocks.",
  ];

  if (loading && !dropoutData) {
    return (
      <div className="analytics-dropout-view" data-testid="analytics-dropout-tab">
        <div className="adr-loading">
          <div className="adr-loading-spinner" />
          <span>Generating dropout risk analysis…</span>
        </div>
      </div>
    );
  }

  if (!dropoutData) {
    return (
      <div className="analytics-dropout-view" data-testid="analytics-dropout-tab">
        <div className="adr-loading">
          <p style={{ margin: 0, fontSize: 18, color: "var(--adr-text)", fontWeight: 900 }}>Dropout Risk Analysis</p>
          <p style={{ margin: 0, maxWidth: 420, textAlign: "center" }}>
            AI-powered analysis of student dropout patterns and intervention planning.
          </p>
          <button type="button" className="adr-btn adr-btn-primary" onClick={onGenerate}>
            Generate Analysis
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-dropout-view" data-testid="analytics-dropout-tab">
      <DashboardPageHeader
        title="Advanced Analytics"
        subtitle={`AI-Powered Dropout Predictions & Intervention Planning · ${scopeLabel}`}
        eyebrow={
          <>
            <svg className="adr-small-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2v4M12 18v4M4.93 4.93 7.76 7.76M16.24 16.24 2.83 2.83M2 12h4M18 12h4M4.93 19.07 7.76 16.24M16.24 7.76l2.83-2.83" />
              <circle cx="12" cy="12" r="4" />
            </svg>
            AI-powered predictions &amp; insights · {scopeLabel}
          </>
        }
        actions={
          <div className="adr-head-actions">
            <button type="button" className="adr-btn">
              <svg className="adr-small-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="M7 10l5 5 5-5" />
                <path d="M12 15V3" />
              </svg>
              Export
            </button>
            <button type="button" className="adr-btn" onClick={onRefresh} disabled={loading}>
              <svg className="adr-small-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                <path d="M3 21v-5h5" />
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                <path d="M16 8h5V3" />
              </svg>
              Refresh
            </button>
          </div>
        }
      />

      <div className="adr-kpi-grid" aria-label="Dropout risk summary">
        <article className="adr-kpi">
          <small>
            Total Blocks
            <svg className="adr-small-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
            </svg>
          </small>
          <strong>{summary?.total_blocks ?? 0}</strong>
          <span>All active in analysis</span>
        </article>
        <article className="adr-kpi adr-danger">
          <small>
            High Risk
            <svg className="adr-small-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
              <path d="M12 9v4M12 17h.01" />
            </svg>
          </small>
          <strong>{summary?.high_risk_count ?? 0}</strong>
          <span className="adr-trend-down">
            {summary?.high_risk_count ? "↑ Needs escalation" : "↓ No red blocks"}
          </span>
        </article>
        <article className="adr-kpi adr-warn">
          <small>
            Medium Risk
            <svg className="adr-small-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          </small>
          <strong>{summary?.medium_risk_count ?? 0}</strong>
          <span>Watchlist blocks</span>
        </article>
        <article className="adr-kpi adr-good">
          <small>
            Low Risk
            <svg className="adr-small-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </small>
          <strong>{summary?.low_risk_count ?? 0}</strong>
          <span className="adr-trend-down">↑ Majority stable</span>
        </article>
        <article className="adr-kpi adr-purple">
          <small>
            Avg Dropout Rate
            <svg className="adr-small-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v18h18" />
              <path d="M7 14l3-3 4 4 5-7" />
            </svg>
          </small>
          <strong>{summary?.avg_dropout_rate ?? 0}%</strong>
          <span>District baseline</span>
        </article>
      </div>

      <section className="adr-command-card" aria-label="Dropout risk command center">
        <div className="adr-command-main">
          <div className="adr-eyebrow">
            <svg className="adr-small-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z" />
            </svg>
            AI dropout risk command center
          </div>
          <h2 className="adr-command-title dashboard-kpi-hero-title">{commandTitle}</h2>
          <p className="adr-command-copy">
            The earlier layout placed long AI text and a table at the same visual level. This redesign turns the tab into
            an executive decision workspace: summary first, risk drivers second, actions third.
          </p>
          <div className="adr-action-strip">
            <div
              className="adr-strip-item clickable"
              role="button"
              tabIndex={0}
              aria-label="Immediate focus: view schools in the highest dropout-rate block"
              onClick={() => setFocusModal("immediate")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setFocusModal("immediate");
                }
              }}
            >
              <b>1. Immediate focus</b>
              <span>
                {highestRate
                  ? `${highestRate.block} has the highest dropout rate at ${highestRate.dropout_rate}% and needs block-level review.`
                  : "Identify the highest-rate block for immediate review."}
              </span>
            </div>
            <div
              className="adr-strip-item clickable"
              role="button"
              tabIndex={0}
              aria-label="Volume focus: view schools with the largest dropout counts"
              onClick={() => setFocusModal("volume")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setFocusModal("volume");
                }
              }}
            >
              <b>2. Volume focus</b>
              <span>
                {highestCount
                  ? `${highestCount.block} has the largest dropout count with ${fmt(highestCount.dropout_count)} learners needing intervention.`
                  : "Prioritize blocks with the largest absolute dropout counts."}
              </span>
            </div>
            <div
              className="adr-strip-item clickable"
              role="button"
              tabIndex={0}
              aria-label="Data quality focus: view schools with migration or remark gaps"
              onClick={() => setFocusModal("data_quality")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setFocusModal("data_quality");
                }
              }}
            >
              <b>3. Data quality focus</b>
              <span>
                {allMigrationZero
                  ? "Check zero-migration records before final risk classification."
                  : "Validate migration and remark completeness before monthly reporting."}
              </span>
            </div>
          </div>
        </div>
        <aside className="adr-command-side">
          <div className="adr-risk-gauge">
            <div className="adr-donut" style={donutStyle}>
              <div className="adr-donut-label">
                <strong>{summary?.avg_dropout_rate ?? 0}%</strong>
                <span>Avg dropout</span>
              </div>
            </div>
            <div className="adr-gauge-copy">
              <h3>District Risk Mix</h3>
              <div className="adr-legend">
                <div className="adr-legend-row">
                  <span>
                    <i className="adr-dot adr-danger" />
                    High risk
                  </span>
                  <b>{summary?.high_risk_count ?? 0} blocks</b>
                </div>
                <div className="adr-legend-row">
                  <span>
                    <i className="adr-dot adr-warn" />
                    Medium risk
                  </span>
                  <b>{summary?.medium_risk_count ?? 0} blocks</b>
                </div>
                <div className="adr-legend-row">
                  <span>
                    <i className="adr-dot" />
                    Low risk
                  </span>
                  <b>{summary?.low_risk_count ?? 0} blocks</b>
                </div>
              </div>
              <div className="adr-confidence">
                Data coverage: {confidencePct}% ·{" "}
                {buildDataStatusChip(
                  { total_blocks: summary?.total_blocks },
                  dropoutData?.generated_at
                )}
              </div>
            </div>
          </div>
        </aside>
      </section>

      <section className="adr-insight-grid">
        <article className="adr-card">
          <div className="adr-card-head">
            <div>
              <div className="adr-card-title">
                <svg className="adr-small-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18" />
                  <path d="M7 13l3-3 4 4 5-8" />
                </svg>
                Priority Block Risk Analysis
              </div>
              <div className="adr-card-subtitle">Ranked by dropout rate and count</div>
            </div>
            <span className="adr-chip">Top {top7.length}</span>
          </div>
          <div className="adr-card-body">
            <div className="adr-priority-list">
              {top7.map((block) => {
                const badge = priorityBadge(block.risk_level, block.dropout_rate);
                const widthPct = Math.round(((block.dropout_rate || 0) / maxRate) * 100);
                return (
                  <div className="adr-priority-row" key={block.block}>
                    <strong>{block.block}</strong>
                    <div className="adr-progress">
                      <i style={{ width: `${widthPct}%` }} />
                    </div>
                    <span className="adr-rate">{block.dropout_rate}%</span>
                    <span className={`adr-badge ${badge.className}`}>{badge.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </article>

        <article className="adr-card">
          <div className="adr-card-head">
            <div>
              <div className="adr-card-title">
                <svg className="adr-small-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2v4M12 18v4M4.93 4.93 2.83 2.83M16.24 16.24 2.83 2.83M2 12h4M18 12h4" />
                  <circle cx="12" cy="12" r="4" />
                </svg>
                AI-Powered Insights
              </div>
              <div className="adr-card-subtitle">Grouped signals instead of long text blocks</div>
            </div>
            <span className="adr-chip">Explainable AI</span>
          </div>
          <div className="adr-card-body">
            <div className="adr-signals">
              {signals.map((signal) => (
                <div className="adr-signal" key={signal.title}>
                  <svg className="adr-small-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                  </svg>
                  <b>{signal.title}</b>
                  <p>{signal.text}</p>
                </div>
              ))}
            </div>
          </div>
        </article>
      </section>

      <section className="adr-lower-grid">
        <article className="adr-card">
          <div className="adr-card-head">
            <div>
              <div className="adr-card-title">
                <svg className="adr-small-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18" />
                  <path d="m19 9-5 5-4-4-3 3" />
                </svg>
                Dropout Trend Forecast
              </div>
              <div className="adr-card-subtitle">Illustrative 6-month outlook</div>
            </div>
            <span className="adr-chip">Predictive</span>
          </div>
          <div className="adr-card-body">
            <div className="adr-trend-chart" aria-label="Dropout trend chart">
              <div className="adr-axis-labels">
                <span>12%</span>
                <span>8%</span>
                <span>4%</span>
                <span>0%</span>
              </div>
              <svg className="adr-trend-svg" viewBox="0 0 420 170" preserveAspectRatio="none" aria-hidden="true">
                <defs>
                  <linearGradient id="adrDropoutFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563eb" stopOpacity="0.22" />
                    <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  d="M0 126 C60 118, 90 116, 140 105 C200 92, 220 76, 280 82 C340 88, 360 72, 420 58 L420 170 L0 170 Z"
                  fill="url(#adrDropoutFill)"
                />
                <path
                  d="M0 126 C60 118, 90 116, 140 105 C200 92, 220 76, 280 82 C340 88, 360 72, 420 58"
                  fill="none"
                  stroke="#2563eb"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
                <path
                  d="M0 138 C70 132, 110 132, 155 124 C215 114, 240 104, 300 105 C360 107, 382 96, 420 88"
                  fill="none"
                  stroke="#14b8a6"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray="8 8"
                />
                <circle cx="420" cy="58" r="7" fill="#2563eb" />
                <circle cx="420" cy="88" r="6" fill="#14b8a6" />
              </svg>
              <div className="adr-month-labels">
                <span>Apr</span>
                <span>May</span>
                <span>Jun</span>
                <span>Jul</span>
                <span>Aug</span>
                <span>Sep</span>
              </div>
            </div>
            <div className="adr-trend-legend">
              <span>
                <i className="adr-dot" style={{ background: "var(--adr-primary)" }} />
                Without intervention
              </span>
              <span>
                <i className="adr-dot" style={{ background: "var(--adr-accent)" }} />
                With planned intervention
              </span>
            </div>
          </div>
        </article>

        <InterventionActionPlan
          aiContent={dropoutData?.ai_insights}
          defaultRoot={defaultRoot}
          defaultRecs={defaultRecs}
          defaultActions={defaultActions}
        />
      </section>

      <section className="adr-card" style={{ marginTop: 18 }}>
        <div className="adr-card-head">
          <div>
            <div className="adr-card-title">
              <svg className="adr-small-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 3h18v18H3z" />
                <path d="M3 9h18" />
                <path d="M9 21V9" />
              </svg>
              Block Risk Detail Table
            </div>
            <div className="adr-card-subtitle">Detailed data remains available, but below the executive summary</div>
          </div>
          <span className="adr-chip">Scrollable in app</span>
        </div>
        <div className="adr-detail-table-wrap">
          <table className="adr-table">
            <thead>
              <tr>
                <th>Block</th>
                <th className="adr-num">Dropout</th>
                <th className="adr-num">Rate</th>
                <th className="adr-num">Risk Score</th>
                <th>Risk</th>
                <th>Suggested Owner</th>
              </tr>
            </thead>
            <tbody>
              {sortedByRate.map((block, index) => {
                const badge = tableRiskBadge(block.risk_level);
                const barWidth = Math.round(((block.dropout_count || 0) / maxCount) * 100);
                return (
                  <tr key={block.block}>
                    <td>{block.block}</td>
                    <td className="adr-num">
                      <span className="adr-student-bar">
                        {fmt(block.dropout_count)}
                        <span className="adr-mini-bar">
                          <i style={{ width: `${barWidth}%` }} />
                        </span>
                      </span>
                    </td>
                    <td className="adr-num">{block.dropout_rate}%</td>
                    <td className="adr-num">{block.risk_score}</td>
                    <td>
                      <span className={`adr-badge ${badge.className}`}>{badge.label}</span>
                    </td>
                    <td>{suggestOwner(block)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <DropoutRiskSchoolsModal
        open={Boolean(focusModal)}
        focus={focusModal}
        onClose={() => setFocusModal(null)}
        scopeLabel={scopeLabel}
        districtPct={summary?.avg_dropout_rate}
      />
    </div>
  );
};

export default AnalyticsDropoutRiskTab;
